import * as Tooltip from '@radix-ui/react-tooltip';
import {
  AlertTriangle,
  CalendarClock,
  Flag,
  PencilLine,
  ShieldAlert,
  TrendingUp,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import type { PositionStatusFlag } from '../../lib/api/endpoints';
import { cn } from '../../lib/utils';

interface FlagConfig {
  label: string;
  description: string;
  className: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
}

const FLAG_CONFIG: Record<PositionStatusFlag, FlagConfig> = {
  NO_STOP: {
    label: 'No stop',
    description: 'No current stop is recorded for this open position.',
    className: 'border-danger/70 bg-danger-subtle text-red-100',
    Icon: ShieldAlert,
  },
  INVALID_STOP: {
    label: 'Invalid stop',
    description: 'The stop is on the wrong side of the current price for this position.',
    className: 'border-danger/70 bg-danger-subtle text-red-100',
    Icon: ShieldAlert,
  },
  DATA_INCOMPLETE: {
    label: 'History incomplete',
    description: 'This symbol has sells before the imported buy history begins.',
    className: 'border-warn/70 bg-warn-subtle text-amber-100',
    Icon: AlertTriangle,
  },
  // Only emitted by API builds that predate the lifetime P&L caveat; remove after rollout.
  HISTORICAL_PNL_INCOMPLETE: {
    label: 'Historical P&L incomplete',
    description:
      'Earlier executions are incomplete, but the current position episode is fully reconstructed.',
    className: 'border-warn/70 bg-warn-subtle text-amber-100',
    Icon: AlertTriangle,
  },
  ORDERING_UNCERTAIN: {
    label: 'Order uncertain',
    description: 'At least one execution has no time, so same-day FIFO uses stable import order.',
    className: 'border-warn/70 bg-warn-subtle text-amber-100',
    Icon: AlertTriangle,
  },
  STOP_MOVED_ADVERSE: {
    label: 'Stop moved',
    description: 'The current stop has been moved adversely from the planned or prior stop.',
    className: 'border-warn/70 bg-warn-subtle text-amber-100',
    Icon: AlertTriangle,
  },
  HELD_PAST_PLAN: {
    label: 'Past plan',
    description: 'The position has been held longer than its swing plan allowed.',
    className: 'border-warn/70 bg-warn-subtle text-amber-100',
    Icon: CalendarClock,
  },
  OVERSIZED: {
    label: 'Oversized',
    description: 'The position is above the default 25% NAV size cap.',
    className: 'border-danger/70 bg-danger-subtle text-red-100',
    Icon: TrendingUp,
  },
  THEME_OVER_CAP: {
    label: 'Theme cap',
    description: 'The position theme is above the default 35% NAV theme cap.',
    className: 'border-danger/70 bg-danger-subtle text-red-100',
    Icon: Flag,
  },
  MANUAL_OVERRIDE: {
    label: 'Manual',
    description: 'This position is using a manual quantity or average-cost override.',
    className: 'border-warn/70 bg-warn-subtle text-amber-100',
    Icon: PencilLine,
  },
};

interface PositionStatusPillsProps {
  flags: PositionStatusFlag[];
}

export function PositionStatusPills({ flags }: PositionStatusPillsProps) {
  if (flags.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-success/50 bg-success-subtle px-2 py-1 text-xs font-medium text-emerald-100">
        <Flag className="h-3.5 w-3.5" aria-hidden="true" />
        Clear
      </span>
    );
  }

  return (
    <Tooltip.Provider delayDuration={150}>
      <div className="flex flex-wrap gap-1.5">
        {flags.map((flag) => {
          const config = FLAG_CONFIG[flag];
          const Icon = config.Icon;
          return (
            <Tooltip.Root key={flag}>
              <Tooltip.Trigger asChild>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded border px-2 py-1 text-xs font-medium',
                    config.className,
                  )}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  {config.label}
                </span>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  sideOffset={6}
                  className="max-w-xs rounded border border-border bg-surface-raised px-3 py-2 text-xs text-text shadow-lg"
                >
                  {config.description}
                  <Tooltip.Arrow className="fill-surface-raised" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          );
        })}
      </div>
    </Tooltip.Provider>
  );
}
